import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, catchError, filter, interval, map, of, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs';

import { GuaranteeApplicationsService } from '../services/guarantee-applications.service';
import * as PrescriptionsActions from './prescriptions.actions';
import { selectPrescriptionsFilters, selectPrescriptionsPage, selectPrescriptionsPageSize } from './prescriptions.selectors';

export const fetchPrescriptionsEffect = createEffect(
  (actions$ = inject(Actions), guaranteeService = inject(GuaranteeApplicationsService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.fetchPrescriptions),
      switchMap(action =>
        guaranteeService.searchGuaranteeApplications(action).pipe(
          map(pagedResponse => PrescriptionsActions.fetchPrescriptionsSuccess({ pagedResponse })),
          catchError(error => of(PrescriptionsActions.fetchPrescriptionsFailure({ error: error.message || 'Failed to fetch prescriptions' })))
        )
      )
    ),
  { functional: true }
);

export const prescriptionsPollingEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store), guaranteeService = inject(GuaranteeApplicationsService)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.startPrescriptionsPolling),
      switchMap(({ interval: pollInterval = 3000, duration = 30000, searchParams }) => {
        let pollCount = 0;
        const maxPolls = Math.ceil(duration / pollInterval);

        return interval(pollInterval).pipe(
          startWith(0),
          takeUntil(actions$.pipe(ofType(PrescriptionsActions.stopPrescriptionsPolling))),
          withLatestFrom(
            store.select(selectPrescriptionsPage),
            store.select(selectPrescriptionsPageSize),
            store.select((state: any) => state.memberHub?.memberProfile?.userId)
          ),
          filter(() => {
            pollCount++;
            const shouldContinue = pollCount <= maxPolls;
            if (!shouldContinue) {
              setTimeout(() => store.dispatch(PrescriptionsActions.stopPrescriptionsPolling()), 0);
            }
            return shouldContinue;
          }),
          switchMap(([_, page, pageSize, userId]) => {
            const params = {
              page,
              pageSize,
              sortBy: 'last_update_date',
              sortDirection: 'desc',
              ...(userId && { userId }),
              ...searchParams,
            };

            return guaranteeService.searchGuaranteeApplications(params).pipe(
              map(pagedResponse => PrescriptionsActions.silentFetchPrescriptionsSuccess({ pagedResponse })),
              catchError(error => {
                console.error('[Prescriptions Polling] Error:', error);
                return of({ type: 'NOOP' });
              })
            );
          })
        );
      })
    ),
  { functional: true }
);

export const refreshPrescriptionsDataEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)): Observable<Action> =>
    actions$.pipe(
      ofType(PrescriptionsActions.refreshPrescriptionsData),
      withLatestFrom(
        store.select(selectPrescriptionsFilters),
        store.select((state: any) => state.memberHub?.memberProfile?.userId)
      ),
      map(([action, filters, userId]) => {
        const searchParams: any = {};

        if (userId) searchParams.userId = userId;
        if (filters?.search && filters.search.length > 3) searchParams.patientName = filters.search;
        if ((action as any).searchParams) Object.assign(searchParams, (action as any).searchParams);

        return PrescriptionsActions.startPrescriptionsPolling({
          interval: 3000,
          duration: 30000,
          searchParams,
        });
      })
    ),
  { functional: true }
);
